import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  UpdateNextShipmentFillDateResponseProto,
} from "@noom/noom-contracts/noom_contracts/healthcare/service/healthcare/medical_shipments_service";
import type { CivilDateProto } from "@noom/noom-contracts/noom_contracts/lib/time/civil_time";

import { updateNextShipmentFillDate } from "../modules/shipping/updateNextShipmentFillDate";

import { useAuthCredentials } from "./useAuthCredentials";

export const useUpdateFillDate = (): {
  updateFillDate: (newFillDate: CivilDateProto, validateOnly?: boolean) => Promise<UpdateNextShipmentFillDateResponseProto | null>;
  loading: boolean;
  error: string | null;
  data: UpdateNextShipmentFillDateResponseProto | null;
} => {
  const { accessCode, accessToken } = useAuthCredentials();
  const [searchParams] = useSearchParams();
  const userTreatmentId = searchParams.get("userTreatmentId");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UpdateNextShipmentFillDateResponseProto | null>(null);

  const updateFillDate = useCallback(async (newFillDate: CivilDateProto, validateOnly: boolean = false) => {
    if (!accessCode || !accessToken) {
      setError("User not authenticated");
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await updateNextShipmentFillDate(
        accessCode,
        accessToken,
        { newFillDate },
        validateOnly,
        userTreatmentId
      );
      setData(response);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update fill date";
      setError(errorMessage);
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [accessCode, accessToken, userTreatmentId]);

  return { updateFillDate, loading, error, data };
};
